import type { Country } from '@atlasguess/engine';
import type { CameraView } from '../config';

/**
 * URL filter slugs for practice modes — the optional 4th URL segment
 * (`/play/:scope/:format/:filter`) that scopes the active pool down to a
 * continent or a single country without spawning a new mode config.
 *
 * Two slug shapes are supported:
 *
 *   - **Continent slugs** — fixed list (`europe`, `north-america`, etc.),
 *     one per `Country.continent` value. Resolves to a `continents: [...]`
 *     fragment overlaid on the mode's base filter.
 *   - **Country slugs** — `slugifyCountryName(country.name)`. Resolves to
 *     an `isos: [country.iso_a2]` fragment.
 *
 * Continents take precedence in case a country name happens to slug to
 * the same string (none currently do; the ordering is a defensive
 * tiebreaker).
 *
 * Same shape will host admin1 filters later (`/play/cities-us/practice/california`)
 * — `resolveFilterSlug` will gain a third branch returning `{ admin1Code }`
 * once admin1 datasets land. Keeping the schema this small means most
 * filter UIs can reuse this resolver verbatim.
 */

/**
 * Slugify a country name into the URL-safe form used in `/play/:scope/:format/:filter`.
 *
 * Algorithm (chosen for round-trip stability and readability):
 *   1. NFD-normalize and strip diacritics — "Côte d'Ivoire" → "Cote d'Ivoire"
 *   2. Lowercase
 *   3. Replace any run of non-[a-z0-9] characters with a single hyphen
 *      — "Cote d'Ivoire" → "cote-d-ivoire"
 *   4. Trim leading / trailing hyphens
 *
 * Examples:
 *   "United States"    → "united-states"
 *   "Côte d'Ivoire"    → "cote-d-ivoire"
 *   "São Tomé and..."  → "sao-tome-and-principe"
 *   "Saint Kitts & N." → "saint-kitts-n"
 */
export function slugifyCountryName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Continent slug → `Country.continent` value. Slugs are the same set the
 * region-quiz routes already use (`europe`, `north-america`, etc.) so a
 * player who knows `/play/countries-europe/quiz` can intuit
 * `/play/countries-world/practice/europe`.
 *
 * Antarctica + open-ocean are intentionally absent — no playable game uses
 * them. Add new entries here when continent values get extended (e.g.
 * splitting "Oceania" into Australasia / Polynesia).
 */
export const CONTINENT_BY_SLUG: Record<string, string> = {
  africa: 'Africa',
  asia: 'Asia',
  europe: 'Europe',
  'north-america': 'North America',
  oceania: 'Oceania',
  'south-america': 'South America',
};

/**
 * Per-continent camera framing keyed by the same slugs as `CONTINENT_BY_SLUG`.
 * Single source of truth for "what does Europe look like centered" — both
 * region quizzes and filtered practice modes read from this so retuning a
 * continent's framing only happens in one place.
 *
 * `zoom` is camera distance from globe center; lower = closer in. Default
 * world view in the engine is 3.2.
 */
export const CONTINENT_CAMERAS: Record<string, CameraView> = {
  africa: { lat: 0, lon: 20, zoom: 2.5 },
  asia: { lat: 35, lon: 90, zoom: 2.8 },
  europe: { lat: 52, lon: 15, zoom: 2.0 },
  'north-america': { lat: 40, lon: -95, zoom: 2.6 },
  oceania: { lat: -25, lon: 140, zoom: 2.6 },
  'south-america': { lat: -15, lon: -60, zoom: 2.3 },
};

/**
 * Pick a sensible camera framing for a country based on its bbox span.
 *
 * Heuristic: zoom = `1.5 + (maxSpanDegrees / 50) * 1.3`, clamped to
 * [1.5, 2.8]. Tuned by eye after dialling back from an earlier closer
 * floor — small countries were arriving framed too tight against their
 * coastlines with no surrounding context. The new floor pulls back
 * enough that even sub-degree countries (Vatican, Monaco) read as
 * "country-on-globe" instead of "polygon close-up".
 *
 *   - tiny (Vatican, Monaco)  → ~1.5 (floor — comfortable surrounding context)
 *   - small (Belgium, Israel) → ~1.6
 *   - medium (France, Spain)  → ~1.8
 *   - large (Brazil, India)   → ~2.5
 *   - huge  (US, China, AU)   → ~2.8 (clamped — pulled all the way back)
 *
 * Antimeridian handling: when a country's bbox crosses 180° (Russia,
 * Fiji, USA-with-Aleutians), `maxLon - minLon` is ~360° which would
 * always clamp to max zoom. Detect that case and fall back to the
 * latitude span — coarse but correct in spirit (Russia ends up at the
 * max-pulled-back zoom regardless, which matches its size).
 */
export function zoomForCountry(country: Country): number {
  const [minLon, minLat, maxLon, maxLat] = country.bbox;
  const latSpan = Math.max(0, maxLat - minLat);
  let lonSpan = Math.max(0, maxLon - minLon);
  if (lonSpan > 180) lonSpan = latSpan;
  const span = Math.max(latSpan, lonSpan);
  return Math.max(1.5, Math.min(2.8, 1.5 + (span / 50) * 1.3));
}

/**
 * Resolved filter — one of `continentName`, `countryIso`, or neither
 * (unresolved slug → empty object, treated as "no extra filter applied").
 *
 * Both fields optional rather than a discriminated union so the caller
 * can spread the result straight into a `CountryFilterCriteria` /
 * `CityFilterCriteria` literal: `{ ...base, ...resolved }`.
 */
export interface ResolvedFilterSlug {
  /** Matches `Country.continent` (e.g. "North America"). */
  continentName?: string;
  /** ISO 3166-1 alpha-2 (e.g. "US"). */
  countryIso?: string;
}

/**
 * Resolve a URL filter slug to its semantic shape.
 *
 *   - `undefined` / empty / unknown → `{}` (caller treats as "no filter")
 *   - Continent slug → `{ continentName }`
 *   - Country slug   → `{ countryIso }`
 *
 * Unknown slugs return `{}` deliberately — the player likely typo'd the
 * URL, and quietly degrading to "no extra filter" lets the rest of the
 * mode still load. Logging would be nice but isn't worth the noise yet.
 *
 * The `allCountries` param is required so the resolver can match any
 * country in the dataset, not just those a particular mode's base
 * filter would surface. Caller typically passes the result of
 * `getCountryList()`.
 */
export function resolveFilterSlug(
  slug: string | undefined,
  allCountries: readonly Country[],
): ResolvedFilterSlug {
  if (!slug) return {};
  const continentName = CONTINENT_BY_SLUG[slug];
  if (continentName) return { continentName };
  const country = allCountries.find((c) => slugifyCountryName(c.name) === slug);
  if (country) return { countryIso: country.iso_a2 };
  return {};
}
